import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { changePassword, login, redeemSetupCode } from '../api/client';
import { BrandMark, Button, ErrorBox, Field, Notice, TestVersionBadge, TextInput } from '../components/ui';

const MIN_PASSWORD = 12;

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-gradient-to-b from-slate-100 to-blue-50 p-4">
      <div className="flex flex-col items-center gap-2 text-center">
        <BrandMark className="h-12 w-12" />
        <p className="text-lg font-semibold text-slate-900">Sale Deed documents</p>
        <p className="max-w-xs text-sm text-slate-600">Prepare sale deeds in Telugu and English.</p>
        <TestVersionBadge />
      </div>
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-md">
        <h1 className="mb-4 text-xl font-semibold">{title}</h1>
        {children}
      </div>
      <p className="max-w-sm text-center text-xs text-slate-500">
        Aadhaar and PAN numbers and ID card photos stay on the computer they are typed on. They are never sent to the server.
      </p>
    </main>
  );
}

function PasswordPair({ value, confirm, onValue, onConfirm, label = 'New password' }: {
  value: string; confirm: string; onValue: (v: string) => void; onConfirm: (v: string) => void; label?: string;
}) {
  const mismatch = confirm.length > 0 && confirm !== value;
  return (
    <>
      <Field label={label} hint={`At least ${MIN_PASSWORD} characters. A short sentence works well.`}>{(id, d) => (
        <TextInput id={id} aria-describedby={d} type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} maxLength={128} value={value} onChange={e => onValue(e.target.value)} />
      )}</Field>
      <Field label="Repeat it" error={mismatch ? 'The two passwords differ' : undefined}>{(id, d) => (
        <TextInput id={id} aria-describedby={d} aria-invalid={mismatch} type="password" autoComplete="new-password" required value={confirm} onChange={e => onConfirm(e.target.value)} />
      )}</Field>
    </>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <Card title="Sign in">
      <form className="flex flex-col gap-3" onSubmit={async e => {
        e.preventDefault();
        setBusy(true); setError(null);
        try { await login(email, password); } catch (err) { setError(err); setPassword(''); } finally { setBusy(false); }
      }}>
        <Field label="Email">{id => <TextInput id={id} type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} />}</Field>
        <Field label="Password">{id => <TextInput id={id} type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />}</Field>
        <ErrorBox error={error} />
        <Button tone="primary" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</Button>
        <p className="text-sm text-slate-600">First time here? <Link to="/setup" className="text-blue-800 underline">Use your setup code</Link></p>
      </form>
    </Card>
  );
}

export function SetupPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // A setup link carries the code after "#", which browsers never send to servers.
  useEffect(() => {
    const m = /(?:^|&)code=([^&]+)/.exec(location.hash.slice(1));
    if (m) {
      setCode(decodeURIComponent(m[1]!));
      history.replaceState(null, '', location.pathname);
    }
  }, []);

  return (
    <Card title="Set up your account">
      <form className="flex flex-col gap-3" onSubmit={async e => {
        e.preventDefault();
        if (password !== confirm) return;
        setBusy(true); setError(null);
        try { await redeemSetupCode(code.trim(), password); void navigate({ to: '/' }); } catch (err) { setError(err); } finally { setBusy(false); }
      }}>
        <Field label="Setup code" hint="From your administrator. It works once.">{(id, d) => (
          <TextInput id={id} aria-describedby={d} required autoComplete="one-time-code" spellCheck={false} value={code} onChange={e => setCode(e.target.value)} />
        )}</Field>
        <PasswordPair value={password} confirm={confirm} onValue={setPassword} onConfirm={setConfirm} />
        <ErrorBox error={error} />
        <Button tone="primary" type="submit" disabled={busy || password !== confirm || password.length < MIN_PASSWORD}>{busy ? 'Setting up…' : 'Set password and sign in'}</Button>
        <p className="text-sm text-slate-600"><Link to="/" className="text-blue-800 underline">Back to sign in</Link></p>
      </form>
    </Card>
  );
}

export function AccountPage() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="mx-auto max-w-md p-6">
      <h1 className="mb-4 text-xl font-semibold">Change password</h1>
      <form className="flex flex-col gap-3" onSubmit={async e => {
        e.preventDefault();
        if (next !== confirm) return;
        setBusy(true); setError(null); setDone(false);
        try { await changePassword(current, next); setDone(true); setCurrent(''); setNext(''); setConfirm(''); } catch (err) { setError(err); } finally { setBusy(false); }
      }}>
        <Field label="Current password">{id => <TextInput id={id} type="password" autoComplete="current-password" required value={current} onChange={e => setCurrent(e.target.value)} />}</Field>
        <PasswordPair value={next} confirm={confirm} onValue={setNext} onConfirm={setConfirm} />
        <ErrorBox error={error} />
        {done && <Notice tone="success">Password changed. Other devices have been signed out.</Notice>}
        <Button tone="primary" type="submit" disabled={busy || next !== confirm || next.length < MIN_PASSWORD}>Change password</Button>
      </form>
    </div>
  );
}
